/**
 * Error Toast Component
 * Provides secure error display without information leakage
 */

import { AlertCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createErrorInfo, type ErrorInfo, type HandleableError } from '@/lib/secure-error-handler';
import { cn } from '@/lib/utils';
import { Button } from './button';

export interface ErrorToastProps {
  error: HandleableError;
  context?: Record<string, any>;
  onDismiss?: () => void;
  autoClose?: number; // Auto close after N seconds (default: 5)
  showDetails?: boolean;
  className?: string;
}

export function ErrorToast({
  error,
  context,
  onDismiss,
  autoClose = 5,
  showDetails = false,
  className,
}: ErrorToastProps) {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(autoClose);

  useEffect(() => {
    if (error) {
      const info = createErrorInfo(error, context);
      setErrorInfo(info);
    }
  }, [error, context]);

  // Auto-close timer
  useEffect(() => {
    if (!autoClose || timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
      if (timeLeft === 1) {
        setIsVisible(false);
        setTimeout(() => onDismiss?.(), 300); // Allow animation to complete
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, autoClose, onDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss?.(), 300);
  };

  if (!errorInfo || !isVisible) return null;

  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-50 max-w-md p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg transition-all duration-300',
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />

        <div className="flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-medium text-red-800">Error Occurred</h4>
              <p className="text-sm text-red-700 mt-1">{errorInfo.userMessage}</p>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {showDetails && (
            <details className="text-xs text-red-600">
              <summary className="cursor-pointer font-medium hover:text-red-800">Technical Details</summary>
              <div className="mt-2 p-2 bg-red-100 rounded border">
                <p>
                  <strong>Code:</strong> {errorInfo.code}
                </p>
                <p>
                  <strong>Time:</strong> {new Date(errorInfo.timestamp).toLocaleString()}
                </p>
              </div>
            </details>
          )}

          {autoClose > 0 && (
            <div className="text-xs text-red-600 flex items-center justify-between">
              <span>Auto-close in {timeLeft}s</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTimeLeft(timeLeft + 10)}
                className="h-5 px-2 text-xs text-red-600 hover:text-red-800"
              >
                +10s
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook for managing error toasts
 */
export function useErrorToast() {
  const [errors, setErrors] = useState<
    Array<{
      id: string;
      error: HandleableError;
      context?: Record<string, any>;
    }>
  >([]);

  const showError = (error: HandleableError, context?: Record<string, any>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setErrors((prev) => [...prev, { id, error, context }]);
  };

  const dismissError = (id: string) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  };

  const clearAllErrors = () => {
    setErrors([]);
  };

  return {
    errors,
    showError,
    dismissError,
    clearAllErrors,
  };
}
